import * as React from 'react';
import { Card, CardGroup, Button, NavDropdown } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import _, { last } from 'lodash';
import classnames from "classnames";
import moment from "moment";
import { unwrapResult } from '@reduxjs/toolkit'

import '../housekeeping.scss'
import * as actions from '../reducer'
import QRReaderForm from "../../../components/QRReaderForm";
import ResidentNoteForm from "../ResidentNoteForm/index";
import RoomHistoryForm from "../RoomHistoryFormV2";
import CommentAndPhotosForm from '../CommentAndPhotosForm'
import { imageCache } from "../../../../common/dataCache";
import Swal, {runWithProgress, SwalForm } from '../../../components/Alert';
import { HKReportStatus, Resident, NoteType, Room, HKReport, AssignmentTemplate, AuditStates } from '../../../../store/models/domain'
import ColorUtils from '../../../../utils/ColorUtils';
import { authProvider } from "../../../../common/auth/AuthProvider";
import { useAppDispatch } from "../../../../store/store"
import { useStore } from "react-redux"
import { loadHousekeepingReports } from "../../../../store/reducers/domain/housekeeping"
import { saveResidentHKNote, saveFilteredRoomIds } from '../../../../store/reducers/domain/facilities'
import AreaFilterRenderItem, {AssignmentFilterRenderItem, AssignmentsFilterOption, filterRoomsByAssignments} from "./AreaFilterRenderItem"
import MomentUtils from '../../../../utils/MomentUtils';


interface Props {
    state: actions.CompState
    dispatch: actions.HkDispatch
    t: any
}

interface RoomBoxComp {
    roomId: string
    onClick(roomId: string, flagState?: string, auditStates?: AuditStates): void
}

const HousekeepingDashboard = (props: Props) => {
    const {
        t, //Translation
        dispatch,
        state
    }: Props = props

    const store = useStore()
    const appDispatch = useAppDispatch()
    const isAdmin = authProvider.hasRole(authProvider.Roles.Nursing);

    React.useEffect(() => {
      // if filtered IDs is not empty, dispatch the save filtered IDs
      // this execution only need during first mount of the component
      if (state.rooms.filteredIds.length > 0 && store.getState().facilities.filteredRoomIds.length === 0) {
        appDispatch(saveFilteredRoomIds(state.rooms.filteredIds));
      }
    }, [])

    const _changeAreaFilter = (level: number, selected: string) => {
        dispatch(actions.applyAreaFilter(level, selected))
    }

    const _changeRoomTypeFilter = (selected: string) => {
        dispatch(actions.applyRoomTypeFilter(selected))
    }

    const _changeAssignmentsFilter = (selected: actions.AssignmentsFilterOption, selectedSchedule?: AssignmentTemplate) => {
        dispatch(actions.applyAssignmentsFilter(selected, selectedSchedule))

        // compute the same filtered result independently, instead of getting the values from state 'coz it may cause a stale state
        const filtered = filterRoomsByAssignments(
            state.rooms.allIds.map(id => state.rooms.byId[id]),
            selected,
            state.assignments,
            state.roomSchedules,
            selectedSchedule
        );
        const filteredIds = filtered.map(r => r.id) ?? [];
        
        appDispatch(saveFilteredRoomIds(filteredIds));

        // force to load the latest data of the rooms
        const facilityId = authProvider.getAccountInfo()?.facility?.id;
        dispatch(actions.performUpdate(facilityId!))
    }

    const _selectRoom = (roomId: string, qrCode?: string) => {        

        let room = state.rooms.byId[roomId ?? ""];
        let roomTypeIds = state.roomTypes.typeMap[room.roomType];
        let roomType = state.roomTypes.byId[roomTypeIds?.find(r => true) ?? ""];

        if(!roomType || (roomType.routines ?? []).length === 0) {
            
            Swal.fire({
                icon: 'error',
                title: t('Could not start cleaning'),
                text: t('Could not start cleaning on this room as cleaning operations are not configured for \'{{roomType}}\' room type.', {roomType: room.roomType}),
                showConfirmButton: true
            })

            return;
        }

        let residentId = state.residentRoomLookup[roomId] ?? {};
        let resident = state.residents.byId[residentId];
       
        if(!!resident){
            _showPreCleanNote(resident, roomId, qrCode)
        } else {
            _selectRoomCore(roomId, qrCode);
        }        
    }

    const _selectRoomCore = (roomId: string, qrCode?: string) => {        

        const room = state.rooms.byId[roomId];
        //const roomSchedule = state.roomSchedules.byRoomId[room.id];      
        const lastReport = _.last(_.orderBy(room.housekeepingHistory ?? [], (h) => h.dateComplete));
       
        if(!!lastReport && lastReport.status === HKReportStatus.Incomplete /*&& 
            // Don't allow continue cleaning if it was started in different or finished time bucket
            (!roomSchedule?.scheduleBucket || 
             (roomSchedule.scheduleType === HKScheduleType.Cron || roomSchedule!.scheduleBucket!.end > moment()) && 
             roomSchedule!.scheduleBucket!.start < moment(lastReport.startDate))*/) {

            const incompleteDateDuration = MomentUtils.getDateTimeframe(lastReport.dateComplete)

            Swal.fire({
                title: `The ${t(`${room.stateCleanType} cleaning was left incomplete ${incompleteDateDuration}.`)}`,
                text: "Do you want to:",
                showCancelButton: true,                
                confirmButtonText: t('Finish last'),
                cancelButtonText: t('Start new'),
                width: "40%",
                customClass: {
                    popup: 'alert-popup-general alert-no-component',
                    confirmButton: 'alert-confirm-btn-general',
                    cancelButton: 'alert-cancel-btn-general'
                }
            })
            .then((result) => {                
                dispatch(actions.selectRoom(roomId, qrCode, !!result.value));
            });
        } else {
            dispatch(actions.selectRoom(roomId, qrCode, false));
        }        
    }

    // onAuditMarking
    const _startAuditMarking = (roomId: string, qrCode?: string) => {        

        let room = state.rooms.byId[roomId ?? ""];
        let roomTypeIds = state.roomTypes.typeMap[room.roomType];
        let roomType = state.roomTypes.byId[roomTypeIds?.find(r => true) ?? ""];

        if(!roomType || (roomType.routines ?? []).length === 0) {
            
            Swal.fire({
                icon: 'error',
                title: t('Could not start Pre-Audit Marking'),
                text: t('Could not start  Pre-Audit Marking on this room as cleaning operations are not configured for \'{{roomType}}\' room type.', {roomType: room.roomType}),
                showConfirmButton: true
            })

            return;
        }
        
        let lastMarkingReport = _.last(state.markingReports.filter(r => r.room.id === roomId));       
        if(!!lastMarkingReport && !room.housekeepingHistory?.some(h => moment(h.dateComplete) > moment(lastMarkingReport?.startDate)) ) {
            // to complete the audit state flow
            dispatch(actions.selectRoom(roomId, qrCode, true, true));
        } else {
            // simple marking state flow
            dispatch(actions.selectRoom(roomId, qrCode, false, true));
        }        
    }

    const _onSaveHkNote = async (residentId: string, hkNoteText: string) => {
        const facilityId = authProvider.getAccountInfo()?.facility?.id;
        appDispatch(saveResidentHKNote({ data: {
            facilityId: facilityId!,
            residentId,
            hkNote: {
                text: hkNoteText,
                staffMember: state.staffMember,
                noteDate: new Date().toISOString()
            }
        }}))
    }
    const _showPreCleanNote = (resident: Resident, roomId: string, qrCode?: string) => {
        const room = state.rooms.byId[roomId];
        return SwalForm.fire({
            html: (<ResidentNoteForm
                values={{ 
                    resident,
                    roomNumber: room.roomNumber,
                    cleanType: room.stateCleanType,
                    preCleanNote: (resident.notes ?? []).filter(n => n.type === NoteType.PreClean).map(n => n.notification).join("\n"),
                 }}
                staffMember={state.staffMember}
                onSaveHkNote={_onSaveHkNote}
                submitLabel={t("Start Cleaning")}
                cancelLabel={t("Cancel")}
                onSubmit={(results: any) => {
                    Swal.close();
                    _selectRoomCore(roomId, qrCode)
                }}
                onCancel={(values: any) => { Swal.close() }} />
            ),
            width: "40%",
        });
    }

    const _onRoomClick = (roomId: string, flagState?: string, auditStates?: AuditStates) => {
        if(authProvider.hasRole(authProvider.Roles.Nursing)){
            _showRoomHistory(state.rooms.byId[roomId], flagState, auditStates);
        } else {
            _selectRoom(roomId);
        }
    }

    const _showRoomHistory = (room: Room, flagState?: string, auditStates?: AuditStates) => {
        let residentId = state.residentRoomLookup[room.id] ?? {}
        let resident = state.residents.byId[residentId] ?? {}
        const roomSchedule = state.roomSchedules.byRoomId[room.id];

        return SwalForm.fire({
            html: (<RoomHistoryForm
                room={room}
                roomTypes={Object.values(state.roomTypes.byId)}
                hkReports={state.hkReports}
                resident={resident}
                roomSchedule={roomSchedule}
                flagState={flagState}
                auditStates={auditStates}
                onNewReport={(room: Room) => {
                    Swal.close();
                    _selectRoom(room.id);
                }}
                onAuditReport={_auditReport}
                onAuditMarking={(room: Room) => {
                    Swal.close();
                    _startAuditMarking(room.id);
                }}
                onClose={() => { Swal.close() }}
                store={store}
            />),
            width: '50vw'
        });
    }

    const _viewCommentPhoto = (hkReport: HKReport | undefined) => {
        if (!hkReport) return;

        Promise.all((hkReport.photos ?? []).map(p => {
            const request = `files/${p.photo}`;
            return imageCache.getText(request);
        }))
            .then(imageCache => {
                SwalForm.fire({
                    title: t('Housekeeper comments & photos'),
                    html: (<CommentAndPhotosForm
                        values={{ ...hkReport, readonly: true }}
                        imageCache={(hkReport.photos ?? []).reduce((obj, cur, i) => { return { ...obj, [cur.id]: imageCache[i] } }, {}) ?? {}}
                        saveLabel={t("Save")}
                        cancelLabel={t("Cancel")}
                        canCancel={true}
                        onSave={(values: any) => {
                            Swal.close();

                            dispatch(actions.updateCommentAndPhotos({
                                comment: values.comment,
                                residentFamilyConcern: values.residentFamilyConcern,
                                maintenance: values.maintenance,
                                pestControl: values.pestControl
                            }, values.photos))
                        }}
                        onCancel={(values: any) => { Swal.close() }}
                        onSendEmail={undefined}
                    />
                    )
                });
            });
    }

    const _openHistoryReport = (reportId: string) => {

        runWithProgress(t("Opening report..."), () => {
            appDispatch(loadHousekeepingReports({ReportId: reportId}))
            .then(unwrapResult)
            .then((r: any) => {               
                const report = r[reportId] as HKReport;
                Swal.close();
                
                if(!report) {
                    throw new Error(`Couldn't find report ${reportId}`)
                }
                
                dispatch(actions.viewHistoryReport(report))
            }).catch(_handleError)
        });
    } 

    const _auditReport = (reportId: string) => {

        runWithProgress(t("Opening report for audit..."), () => {
            appDispatch(loadHousekeepingReports({ReportId: reportId}))
            .then(unwrapResult)
            .then((r: any) => {               
                const report = r[reportId] as HKReport;
                Swal.close();
                
                if(!report) {
                    throw new Error(`Couldn't find report ${reportId}`)
                }
                
                dispatch(actions.auditReport(report))
            }).catch(_handleError)
        });
    } 
    
    const _handleError = (error: any) => {
        Swal.fire({
            icon: 'error',
            title: t('Error: ' + (error.response?.data?.errors || error.message)),
            showConfirmButton: true
        })
    }

    const _scanQR = () => {
        SwalForm.fire({
            title: t('Scan QR Code'),
            html: (<QRReaderForm
                values={{ qrCode: '' }}
                submitLabel={t("OK")}
                cancelLabel={t("Cancel")}
                onSubmit={(results: any) => {
                    const qrCode = results.qrCode
                    for (const roomId of state.rooms.allIds) {
                        const room = state.rooms.byId[roomId]
                        if (room && room.qrCode === qrCode) {
                            _selectRoom(roomId, qrCode)
                            break
                        }
                    }
                    Swal.close()
                }}
                onCancel={(values: any) => { Swal.close() }} />
            )
        });
    }

    
    const renderTooltip = (lastCleaningHistory: any, cleanType: string) => {
        return (
            <Tooltip id="button-tooltip" >
                <div>{t('Last Cleaning')}:</div>
                <div>{t('Clean type')}: {lastCleaningHistory?.cleanType?? cleanType?? ' - '}</div>
                <div>{t('Clean date')}: {(lastCleaningHistory?.dateComplete ? moment(lastCleaningHistory?.dateComplete).format("YYYY-MM-DD HH:mm"): ' - ')}</div>
                <div>{t('Housekeeper')}: {lastCleaningHistory?.staffMember?? ' - '}</div>
            </Tooltip>
        )
    }

    const RoomBoxRenderItem: React.FunctionComponent<RoomBoxComp> = ({ roomId, onClick }) => {
        let room = state.rooms.byId[roomId] ?? {}
        let residentId = state.residentRoomLookup[roomId] ?? {}
        let resident = state.residents.byId[residentId] ?? {}
        let filtered = !(state.roomTypeFilter.selected === 'All Room Types' || state.roomTypeFilter.selected === room.roomType)
        if (!filtered) {
            for (let i = 0; i < state.areaFilters.length; i++) {
                let filter = state.areaFilters[i]
                if (filter.selected === 'Everything') break;
                let area = room.facilityArea[filter.level]
                if (!area) break;
                filtered = area !== filter.selected
                if (filtered) break;
            }
        }
        if (filtered) return null

        const roomTypeIds = state.roomTypes.typeMap[room.roomType];
        const roomType = state.roomTypes.byId[roomTypeIds?.find(r => true) ?? ""];
        const roomState = !!room.roomState ? roomType?.roomStates?.find(s => s.stateName === room.roomState) : undefined;
  

        const roomSchedule = state.roomSchedules.byRoomId[room.id];
        const scheduledClean = roomSchedule?.cleanType;

        // Check if there is incomplete report
        const now = moment();
        const today = moment(now).startOf("day");
        const lastHistory = _.last(_.orderBy((room.housekeepingHistory ?? []), (h) => h.dateComplete));
        let incompleteReport = state.hkReports.find(r => r.id === lastHistory?.reportId);
        
        let isIncomplete = incompleteReport?.status === HKReportStatus.Incomplete /*&& 
            // Check incompleteReport belongs to current bucket            
            (!roomSchedule?.scheduleBucket || 
            // Don't check that bucket is completer for Cron
            (roomSchedule.scheduleType === HKScheduleType.Cron || roomSchedule!.scheduleBucket!.end > now) && roomSchedule!.scheduleBucket!.start < moment(incompleteReport.startDate))*/;


        // Complete if there are completed clean for today
        let todaysHistory = _.orderBy(room.housekeepingHistory ?? [], (h) => h.dateComplete).filter(r => moment(r.dateComplete) >= today);
        
        const lastCleaningHistory = _.last(todaysHistory);
        const isDeclined = lastCleaningHistory?.status === HKReportStatus.Declined
        let isComplete = lastCleaningHistory?.status === HKReportStatus.Complete && (!scheduledClean || (lastCleaningHistory?.priority ?? 0) >= (scheduledClean.priority ?? 0)); 
        
        // NOTE: AUDIT FLAG DESCRIPTION, REFERENCE ON HOW THE AUDIT FLAGS ARE IMPLEMENTED
        // ─── TWO INDEPENDENT AUDIT JOURNEYS ───────────────────────────────────────
        //
        // The audit panel hosts two separate journeys:
        //
        //   OBSERVATION JOURNEY — single event.
        //     Supervisor watches the housekeeper clean. One click, one completion.
        //     No pre-state, no waiting state. Throws "Audit Complete" until midnight.
        //
        //   MARKING JOURNEY — multi-stage. Four mutually exclusive states:
        //     isMarkingJourneyComplete   — audit completed today
        //     isMarkingJourneyActive     — marked, not complete, not orphaned, not inactive
        const allHkCleans = _.orderBy(
            state.hkCleans.filter(c => c.room.id === room.id),
            (c) => c.startDate,
            'desc'
        );
        const [lastHkClean] = allHkCleans;
        
        const markingIsValid = !!room.lastMarkingReport?.startDate &&
            moment(room.lastMarkingReport.startDate).isSameOrAfter(moment().subtract(1, 'day').startOf('day'));
        
        // isMarkingAuditReady — room was cleaned after marking and Dan's merge linked them.
        // auditMarkingId on lastHkClean is the proof the clean belongs to this journey.
        // should be no audit report date == means audit is not completed yet
        const isMarkingAuditReady = markingIsValid && (!!lastHkClean &&
            !!lastHkClean?.auditMarkingId &&
            !lastHkClean?.auditReportDate &&
            lastHkClean.status !== "Declined" &&
            (!!room.lastMarkingReport?.startDate && moment(lastHkClean.startDate).isSameOrAfter(moment(room.lastMarkingReport.startDate))));
                
        // Pre-Audit Marked = isMarkingJourneyActive && !isMarkingAuditReady.
        // Derived from the journey state so it can never disagree with the four-state
        // mutual exclusivity. If the journey is active but not ready, it's pre-marked.
        const isPreAuditMarked = markingIsValid &&
                !isMarkingAuditReady &&
                !!lastHkClean && moment(lastHkClean.startDate).isBefore(moment(room?.lastMarkingReport?.startDate) )//&&
                
        // isMarkingJourneyActive — marking exists, journey not complete, not orphaned, not inactive.
        // This is the only state where sub-states apply.
        const isMarkingJourneyActive = isPreAuditMarked || isMarkingAuditReady;
        
        const isAuditCompleted = !!lastHkClean?.auditReportDate &&
            moment(lastHkClean.auditReportDate).isSame(moment(), 'day');

        // ─── Dashboard Flag Logic ─────────────────────────────────────────────────
        // Priority order matches the state diagram exactly.
        // Audit states are evaluated before incomplete/missed so they are never blocked.
        // React will only recalculate when a dependency changes.
        const isFacilityAssistant = authProvider.hasRole(authProvider.Roles.FacilityAssistant);
        
        
        let style: any = {};
        // Define room color        
        let color: string | undefined = undefined; 
        if(isIncomplete) {    
            let roomType = state.roomTypes.byId[incompleteReport!.roomType.id];
            if (!roomType) roomType = state.roomTypes.byId[roomTypeIds?.find(r => true) ?? ""]
            color = roomType?.routines?.find(c => room.stateCleanType ? c.cleanType === room.stateCleanType : incompleteReport?.roomType?.routines?.some(r => r.cleanType === c.cleanType))?.color;
        } else if(isComplete) {
            color = "#C5E379";
        } else if(!!scheduledClean) {
            color = scheduledClean.color;
        } else {
            // Take default color
            let roomTypeIds = state.roomTypes.typeMap[room.roomType]
            let roomType = state.roomTypes.byId[roomTypeIds?.find(r => true) ?? ""]
            color = roomType?.routines?.find(c => c.isDefault === true)?.color;
        }
        
        if(!!color) {
            style = {
                ...style,
                backgroundColor: color,
                color: ColorUtils.getContrastColor(color)
            }
        }

        let headerStyle: any = isComplete ? style : {}; 
        if(!!roomState) {
            color = roomState.color;
            headerStyle = {                
                backgroundColor: color,
                borderColor: roomState.borderColor, 
                borderStyle: roomState.style,
                borderWidth: "4px"
            };

            if(!!color) {
                headerStyle.color = ColorUtils.getContrastColor(color)
            }
        }

        
        let header = (<span className="smallText staffName">{(isComplete ? lastHistory?.staffMember : "")}</span>);
        // let header = (<span className="smallText staffName"></span>);
        let headerClass = "";
        let flagState = '';

        // Priority order — highest to lowest:
        // 1. Declined         — resident refused, all activity stops
        // 2. Audit Complete   — audit journey finished (Stage 3)
        // 3. Audit Ready      — cleaned after marking, ready to complete (Stage 2)
        // 4. Pre-Audit Marked — marked, waiting on housekeeper (Stage 1B)
        // 5. Incomplete       — hou  sekeeper was there but didn't finish
        // 6. Missed           — housekeeper did not show up
        // 7. Due Soon         — room is approaching its scheduled clean
        // 8. Room State       — precaution, vacant etc. (ambient background info)
        if (isPreAuditMarked && isFacilityAssistant) {
            // Stage 1B — Marked, waiting on housekeeper. Persists across day resets.
            header = (<>{t('Pre-Audit Marked')}</>);
            headerClass = "roomBoxTitleMarked";
            flagState = 'pre-marked';
        }

        else if (isMarkingAuditReady && isFacilityAssistant) {
            // Stage 2 — Room cleaned after marking. Ready for auditor to complete.
            // Overwrites Incomplete per state diagram.
            header = (<>{t('Audit ready')}</>);
            headerClass = "roomBoxTitleAuditReady";
            flagState = 'audit-ready';
        }

        else if (isAuditCompleted && isFacilityAssistant) {
            // Stage 3 — Audit done for today. Visible to auditors and supervisors only.
            header = (<>{t('Audit Complete')}</>);
            headerClass = "roomBoxTitleComplete";
            flagState = 'audit-complete';
        }

        else if (isDeclined) {
            // Resident refused — audit journey paused, nothing to audit.
            header = (<>{t('DECLINED')}</>);
            headerClass = "roomBoxTitleDeclined";
        }
        
        else if (isIncomplete) {
             // Housekeeper was present but did not complete the clean.
             header = (<>{t('INCOMPLETE')}</>);
            headerClass = "roomBoxTitleIncomplete";
        }
           
        else if (roomSchedule?.missed) {
            // Housekeeper did not show up.
            header = (<>{t('MISSED')}</>);
            headerClass = "roomBoxTitleMissed";
        }
            
        else if (roomSchedule?.dueSoon) {
            // Room is approaching its scheduled clean time.
            header = (<>{t('DUE SOON')}</>);
            headerClass = "roomBoxTitleDueSoon";
        }
            
        else if (!!roomState) {
            // Ambient room state — precaution type, vacant etc.
            header = (<>{roomState.stateName}</>);
            headerClass = "roomBoxTitleState";
        }
         
        let hasComment = (incompleteReport?.comment?.length ?? 0) > 0;
        let hasPhotos = (incompleteReport?.photos?.length ?? 0) > 0;

        const overlayProps = isAdmin? {
            delay: { show: 200, hide: 100 }
        } : { show: false }

        const auditStates = {
            isMarkingJourneyActive,
            isMarkingAuditReady,
            isPreAuditMarked
        }
        return (
            <OverlayTrigger placement="bottom" overlay={renderTooltip(lastHistory, roomSchedule?.cleanType?.cleanType)} {...overlayProps}>
                <Card key={"dr_" + room.id} className="roomBox" style={style} 
                    ref={(node: any) => {
                        if (node && style.borderColor) {
                            node.style.setProperty("border-color", style.borderColor, "important");
                            node.style.setProperty("border-style", style.borderStyle, "important");
                            node.style.setProperty("border-width", style.borderWidth, "important");
                        }
                    }}>
                    
                        <div className="roomBoxClickMask" onClick={e => onClick(roomId, flagState, auditStates)}></div>
                    
                    <div className={classnames("roomBoxTitle", headerClass, {
                        roomBoxTitleInvisible: headerClass === "", 
                        roomBoxTitleIncomplete: isIncomplete && !roomState
                        })} style={headerStyle} >
                        {header}
                    </div>
                    <div className="roomBoxNumber"><h5 style={{...style, borderWidth: '0'}}>{room.roomNumber}</h5></div>

                    <div className="roomBoxName smallText">
                        <p>{(resident?.namePrefix ?? '') + ' ' + (resident.firstName ?? '')}</p>
                        <p>{(resident.lastName ?? '') + (!!resident.nickName ? (' (' + resident.nickName + ')') : '')}</p>                    
                    </div>

                    {resident.fallRisk === true &&<div className="roomBoxIcon iconF"><p>F</p></div>}
                    {resident.exitSeeking === true &&<div className="roomBoxIcon iconE"><p>E</p></div>}
                    {resident.sexuallyAbusive === true &&<div className="roomBoxIcon iconS"><p>S</p></div>}
                    {resident.verballyAbusive === true &&<div className="roomBoxIcon iconV"><p>V</p></div>}
                    {resident.physicallyAggressive === true && <div className="roomBoxIcon iconP"><p>P</p></div>}
                    {hasComment === true && <svg className="commentBoxIcon icon" onClick={e => _viewCommentPhoto(incompleteReport)}><use xlinkHref={`/assets/sprite-defs-20241009.svg#sprite-comment`} /></svg>}
                    {hasPhotos === true && <svg className="cameraBoxIcon icon" onClick={e => _viewCommentPhoto(incompleteReport)}><use xlinkHref={`/assets/sprite-defs-20241009.svg#sprite-camera`} /></svg>}
                    <p className="smallText">{room.roomType}</p>
                    
                </Card>
                </OverlayTrigger>
        );
    };

    return (
        <>
            <div className="d-flex align-items-center justify-content-between" style={{position: "relative"}}>
                {!(!!state.staffMember.restrictedArea && 
                    ((state.staffMember.restrictedArea as AssignmentsFilterOption) !== AssignmentsFilterOption.OtherSchedules || 
                    state.staffMember.restrictedAreaAssignmentId === state.assignmentsFilter.selectedSchedule?.id)) && <div className="d-flex">                            
                    <div className="mr-1">                        
                        <AssignmentFilterRenderItem 
                            selected={state.assignmentsFilter.selected} options={state.assignmentsFilter.options} 
                            selectedSchedule={state.assignmentsFilter.selectedSchedule} otherSchedules={state.assignmentsFilter.otherSchedules}
                            onChange={(selected, schedule) => _changeAssignmentsFilter(selected, schedule)} />
                    </div>                    
                    <div className="mr-1">
                        <AreaFilterRenderItem key="filter_roomType" selected={state.roomTypeFilter.selected} defaultSelection="All Room Types"
                            options={state.roomTypeFilter.options} onChange={s => _changeRoomTypeFilter(s)} />
                    </div>
                    {state.areaFilters.map(filter => (
                    <AreaFilterRenderItem key={"filter_" + filter.level} selected={filter.selected} options={filter.options} onChange={s => _changeAreaFilter(filter.level, s)} />
                    ))}
                </div>}                
                <div className="d-flex">                    
                    <Button className="mx-2 p-0" variant="transparent" onClick={() => _scanQR()}><i className="h3 m-0 feather icon-camera" /></Button>
                </div>
            </div>
            {state.rooms.filteredIds.length > 0 ? <CardGroup className="d-flex flex-wrap">
                {state.rooms.filteredIds.map(roomId => (
                    <RoomBoxRenderItem key={"rb_" + roomId} roomId={roomId} onClick={_onRoomClick} />
                ))}
            </CardGroup>
            : <div className="noRoomsContainer d-flex align-items-center justify-content-center">
                <div>{t('All rooms are filtered out.')}</div>
            </div>}
        </>
    );
}

export default HousekeepingDashboard;
